import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/apiService';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const TechnicalStaffManagement = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const staffTypes = [
    { value: 'all', label: 'ყველა პერსონალი' },
    { value: 'medical', label: 'სამედიცინო პერსონალი' },
    { value: 'technical', label: 'ტექნიკური სპეციალისტები' }
  ];

  useEffect(() => {
    loadStaff();
  }, [selectedType]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      if (!token) {
        console.error('No auth token found');
        return;
      }

      const url = selectedType === 'all' 
        ? '/api/technical-staff/' 
        : `/api/technical-staff/?staff_type=${selectedType}`;

      const response = await fetch(getApiUrl(url), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStaff(data);
      } else {
        console.error('Failed to load technical staff');
      }
    } catch (error) {
      console.error('Error loading technical staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ პერსონალის წაშლა?')) {
      return;
    }

    try {
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const response = await fetch(getApiUrl(`/api/technical-staff/${staffId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setStaff(staff.filter(s => s.id !== staffId));
      } else {
        alert('პერსონალის წაშლა ვერ მოხერხდა');
      }
    } catch (error) {
      console.error('Error deleting technical staff:', error);
      alert('შეცდომა პერსონალის წაშლისას');
    }
  };

  const filteredStaff = staff.filter(member => 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStaffTypeLabel = (staffType) => {
    const type = staffTypes.find(t => t.value === staffType);
    return type ? type.label : staffType;
  };

  const getStaffTypeColor = (staffType) => {
    switch (staffType) {
      case 'medical': return 'bg-red-100 text-red-800';
      case 'technical': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <WrenchScrewdriverIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-georgian">
                  ტექნიკური პერსონალის მართვა
                </h1>
                <p className="text-sm text-gray-600 font-georgian">
                  ტექნიკური პერსონალის დამატება, რედაქტირება და წაშლა
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/_panel-7c39f8/technical-staff/new')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              ახალი პერსონალი
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  პერსონალის ტიპი
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  {staffTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ძებნა
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="პერსონალის სახელი ან პოზიცია..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Staff List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-4">
              ტექნიკური პერსონალი ({filteredStaff.length})
            </h3>
            
            {filteredStaff.length === 0 ? (
              <div className="text-center py-12">
                <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">
                  პერსონალი ვერ მოიძებნა
                </h3>
                <p className="mt-1 text-sm text-gray-500 font-georgian">
                  დაამატეთ ახალი პერსონალი ან შეცვალეთ ძებნის კრიტერიუმები
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredStaff.map((member) => (
                  <div key={member.id} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {member.name.charAt(0)}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/_panel-7c39f8/technical-staff/${member.id}/edit`)}
                          className="text-gray-400 hover:text-primary-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 font-georgian text-lg mb-2">
                      {member.name}
                    </h3>
                    
                    <div className="space-y-2">
                      <p className="text-sm text-primary-600 font-georgian font-medium">
                        {member.position}
                      </p>
                      
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStaffTypeColor(member.staff_type)}`}>
                        {getStaffTypeLabel(member.staff_type)}
                      </span>
                      
                      {member.age && (
                        <p className="text-sm text-gray-600 font-georgian">
                          <span className="font-medium">ასაკი:</span> {member.age} წელი
                        </p>
                      )}
                      
                      <p className="text-sm text-gray-600 font-georgian">
                        <span className="font-medium">ეროვნება:</span> {member.nationality}
                      </p>
                      
                      {member.experience && (
                        <p className="text-sm text-gray-600 font-georgian">
                          <span className="font-medium">გამოცდილება:</span> {member.experience}
                        </p>
                      )}
                      
                      {member.specialization && (
                        <p className="text-sm text-gray-600 font-georgian">
                          <span className="font-medium">სპეციალიზაცია:</span> {member.specialization}
                        </p>
                      )}
                    </div>
                    
                    {member.qualifications && member.qualifications.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 font-georgian mb-1">
                          კვალიფიკაციები:
                        </p>
                        <ul className="text-xs text-gray-600 font-georgian space-y-1">
                          {member.qualifications.slice(0, 2).map((qual, idx) => (
                            <li key={idx} className="flex items-center">
                              <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                              {qual}
                            </li>
                          ))}
                          {member.qualifications.length > 2 && (
                            <li className="text-gray-500">
                              +{member.qualifications.length - 2} სხვა
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {member.description && (
                      <p className="mt-3 text-sm text-gray-600 font-georgian line-clamp-2">
                        {member.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TechnicalStaffManagement;
